/**
 * Document API Hooks
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import {
  Document,
  DocumentCreate,
  DocumentListResponse,
  DocumentSortBy,
  BodyUploadDocumentApiDocumentsUploadPost,
  BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut,
  BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut,
  BulkDocumentDeleteRequest,
  BulkDocumentPermissionsRequest,
  BulkDocumentsResponse,
} from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { uploadWithProgress } from '../../api/uploadWithProgress';
import { createQueryHook, createMutationHook } from './factories';

// --- Types ---

// Using the API-generated type for permissions updates
type DocumentPermissionsUpdate = BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut;

interface UploadDocumentParams {
  file: File;
  title?: string;
  aclSettings?: {
    visibility?: string | null;
    purpose?: string | null;
    tags?: string | null;
    category?: string | null;
    shared_with_users?: string | string[] | null;
    shared_with_groups?: string | string[] | null;
    legal_basis?: string | null;
  };
  textExtractionProvider?: string;
  /**
   * Duplicate-handling mode. "prompt" (default) makes the backend return 409
   * with a duplicate signal so the UI can ask the user; "update" re-uses the
   * existing document; "copy" force-creates a distinct document.
   */
  onDuplicate?: 'prompt' | 'update' | 'copy';
  /** Real byte progress (0..100) from the XHR upload stream. */
  onProgress?: (percent: number) => void;
  /** Aborts the in-flight request; the mutation rejects with status 0. */
  signal?: AbortSignal;
}

interface UpdateDocumentFileParams {
  documentId: string;
  file: File;
  title?: string;
  aclSettings?: {
    visibility?: string | null;
    purpose?: string | null;
    tags?: string | null;
    shared_with_users?: string | string[] | null;
    shared_with_groups?: string | string[] | null;
  };
  textExtractionProvider?: string;
}

interface SuccessMessage {
  message: string;
}

interface DocumentsParams {
  skip?: number;
  limit?: number;
  search?: string;
  category?: string;
  source_type?: string;
  visibility?: string;
  owner_id?: string;
  sort_by?: DocumentSortBy;
  created_at_gte?: string;
  created_at_lte?: string;
  updated_at_gte?: string;
  updated_at_lte?: string;
  shared_with_group?: string;
  shared_with_user?: string;
  exclude_owned?: boolean;
  organizationId?: string;
}

// --- Query Hooks ---
const toCsv = (value: string | string[] | null | undefined): string | null | undefined => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(',') : null;
  }
  return value;
};

/**
 * Fetches documents list with filters and pagination.
 * Automatically organization-scoped. Superadmins can specify organizationId.
 * Returns paginated response with documents array and total count.
 */
export const useDocuments = createQueryHook<DocumentListResponse, DocumentsParams>({
  queryKey: (params) => ['documents', params] as const,
  queryFn: (apiClient, params) => {
    const { organizationId, ...queryParams } = params;

    const headers: Record<string, string> = {};
    if (organizationId) {
      headers['X-Organization-Id'] = organizationId;
    }

    return apiClient.getDocuments(
      queryParams,
      { headers }
    ).then(response => response.data);
  },
  logContext: 'GET /api/documents',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/documents', 200, {
      dataLength: data?.documents?.length ?? 0,
      total: data?.total ?? 0,
      result: data
    });
  },
});

/**
 * Fetches a single document by ID.
 */
export const useDocument = createQueryHook<Document, string>({
  queryKey: (documentId) => ['documents', documentId] as const,
  queryFn: (apiClient, documentId) =>
    apiClient.getDocument(documentId).then(response => response.data),
  logContext: 'GET /api/documents/:id',
  staleTime: QUERY_STALE_TIME.LONG,
});

/**
 * Fetches available text extraction providers.
 */
export const useTextExtractionProviders = createQueryHook<string[], void>({
  queryKey: () => ['text-extraction', 'providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminTextExtractionConfigProvidersGet().then(response =>
      response.data
        .map(provider => (typeof provider.label === 'string' ? provider.label : undefined))
        .filter((label): label is string => Boolean(label))
    ),
  logContext: 'GET /api/admin/text-extraction-config/providers',
  staleTime: QUERY_STALE_TIME.LONG,
});

// --- Mutation Hooks ---

/**
 * Creates a new document from text content.
 */
export const useCreateDocument = createMutationHook<Document, DocumentCreate>({
  mutationFn: (apiClient, documentData) =>
    apiClient.createDocument(documentData).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'POST /api/documents',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_created', 'useDocuments');
  },
});

/**
 * Uploads a document file.
 *
 * Goes through the XHR helper instead of apiClient.uploadDocument so the
 * dialog gets real byte progress and can abort. The query invalidation is
 * deliberately NOT configured here: the multi-file runner in useDocumentDialog
 * invalidates ['documents'] once after the whole queue, not once per file.
 */
export const useUploadDocument = createMutationHook<Document, UploadDocumentParams>({
  mutationFn: (_apiClient, params) => {
    const { file, title, aclSettings, textExtractionProvider, onDuplicate, onProgress, signal } = params;

    const uploadData: BodyUploadDocumentApiDocumentsUploadPost = {
      file,
      title: title ?? null,
      category: aclSettings?.category,
      tags: aclSettings?.tags,
      visibility: aclSettings?.visibility,
      shared_with_users: toCsv(aclSettings?.shared_with_users),
      shared_with_groups: toCsv(aclSettings?.shared_with_groups),
      legal_basis: aclSettings?.legal_basis,
      on_duplicate: onDuplicate ?? 'prompt',
    };

    const payload: Record<string, unknown> = textExtractionProvider
      ? { ...uploadData, text_extraction_provider: textExtractionProvider }
      : { ...uploadData };

    return uploadWithProgress<Document>('/api/documents/upload', payload, { onProgress, signal });
  },
  logContext: 'POST /api/documents/upload',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_uploaded', 'useDocuments');
  },
});

/**
 * Updates an existing document.
 * Note: The API expects DocumentCreate type for updates
 */
export const useUpdateDocument = createMutationHook<
  Document,
  { documentId: string; data: DocumentCreate }
>({
  mutationFn: (apiClient, { documentId, data }) =>
    apiClient.updateDocument(documentId, data).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Updates a document file.
 */
export const useUpdateDocumentFile = createMutationHook<Document, UpdateDocumentFileParams>({
  mutationFn: (apiClient, params) => {
    const { documentId, file, title, aclSettings, textExtractionProvider } = params;

    const uploadData: BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut = {
      file,
      title: title ?? null,
      visibility: aclSettings?.visibility,
      tags: aclSettings?.tags,
      // Sharing edited in the same dialog must survive a file replacement;
      // undefined keeps the existing lists, an empty string clears them.
      shared_with_users: toCsv(aclSettings?.shared_with_users),
      shared_with_groups: toCsv(aclSettings?.shared_with_groups),
    };

    const payload = textExtractionProvider
      ? ({ ...uploadData, text_extraction_provider: textExtractionProvider } as BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut)
      : uploadData;

    return apiClient.updateDocumentFile(documentId, payload).then(response => response.data);
  },
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/file',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_file_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Deletes a document.
 */
export const useDeleteDocument = createMutationHook<SuccessMessage, string>({
  mutationFn: (apiClient, documentId) =>
    apiClient.deleteDocument(documentId).then(response => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'DELETE /api/documents/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, documentId) => {
    log.user.action('document_deleted', 'useDocuments', { documentId });
  },
});

/**
 * Bulk actions on a selection of documents. The server checks every document
 * the way the single routes do and reports each outcome; the result lists what
 * was refused, so the page can say so instead of pretending all went through.
 */
export const useDeleteDocumentsBulk = createMutationHook<BulkDocumentsResponse, BulkDocumentDeleteRequest>({
  mutationFn: (apiClient, request) =>
    apiClient.deleteDocumentsBulk(request).then(response => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'POST /api/documents/batch/delete',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('documents_bulk_deleted', 'useDocuments', { succeeded: data.succeeded, failed: data.failed });
  },
});

export const useUpdateDocumentsPermissionsBulk = createMutationHook<BulkDocumentsResponse, BulkDocumentPermissionsRequest>({
  mutationFn: (apiClient, request) =>
    apiClient.updateDocumentsPermissionsBulk(request).then(response => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'POST /api/documents/batch/permissions',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('documents_bulk_permissions', 'useDocuments', { succeeded: data.succeeded, failed: data.failed });
  },
});

interface ReprocessResponse {
  document_id: string;
  job_id: string;
  status: string;
}

/**
 * Re-runs extraction and indexing for a document (owner or admin). The
 * route is newer than the generated client, so it is called directly.
 */
export const useReprocessDocument = createMutationHook<ReprocessResponse, string>({
  mutationFn: (apiClient, documentId) =>
    apiClient
      .request<ReprocessResponse>({
        path: `/api/documents/${documentId}/reprocess`,
        method: 'POST',
        secure: true,
        format: 'json',
      })
      .then((response) => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'POST /api/documents/:id/reprocess',
  logMethod: 'POST',
  onSuccess: (_data, documentId) => {
    log.user.action('document_reprocess_requested', 'useDocuments', { documentId });
  },
});

/**
 * Updates document permissions.
 */
export const useUpdateDocumentPermissions = createMutationHook<
  Document,
  { documentId: string; permissions: DocumentPermissionsUpdate }
>({
  mutationFn: (apiClient, { documentId, permissions }) =>
    apiClient.updateDocumentPermissions(documentId, permissions).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/permissions',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('document_permissions_updated', 'useDocuments', { documentId: variables.documentId });
  },
});
